import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, ImagePlus, ScanText, Sparkles, PenTool, UserRoundX } from "lucide-react";

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-[var(--tg-border)] last:border-0">
    <Label className="label-caps">{label}</Label>
    <div className="w-1/2">{children}</div>
  </div>
);

export default function Branding() {
  const [cfg, setCfg] = useState(null);
  const [logoVer, setLogoVer] = useState(0);
  const [ocr, setOcr] = useState(null);
  const fileRef = useRef();

  const load = async () => {
    const [b, o] = await Promise.all([api.get("/branding"), api.get("/imagetext")]);
    setCfg(b.data);
    setOcr(o.data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (next) => {
    const merged = { ...cfg, ...next };
    setCfg(merged);
    const { has_logo, ...payload } = merged;
    await api.put("/branding", payload);
  };

  const saveOcr = async (next) => {
    const merged = { ...ocr, ...next };
    setOcr(merged);
    await api.put("/imagetext", merged);
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    await api.post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Logo uploaded");
    setLogoVer((v) => v + 1);
    load();
  };

  if (!cfg) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5 w-full max-w-[1800px]">
      <div className="lg:col-span-2 xl:col-span-3 rounded-lg border border-[var(--tg-border)] p-4 text-sm">Discover recurring text, links and signatures with <Link to="/channel-scanner" className="text-[var(--c-sky)] hover:underline">Channel Scanner →</Link><span className="block mt-1 text-xs text-[var(--tg-muted)]">Scanner V1 analyses message structures. Image OCR and logo detection are separate branding tools.</span></div>
      <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="font-head font-bold text-lg">Watermark Engine</div>
          <Switch
            checked={cfg.enabled}
            onCheckedChange={(v) => save({ enabled: v })}
            data-testid="toggle-watermark"
          />
        </div>
        <p className="text-sm text-[var(--tg-faint)] mb-4">Applied to every copied image before sending.</p>

        <Row label="Mode">
          <Select value={cfg.mode} onValueChange={(v) => save({ mode: v })}>
            <SelectTrigger data-testid="select-wm-mode" className="bg-[var(--tg-bg)] border-[var(--tg-border)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
              <SelectItem value="corner">Corner Logo</SelectItem>
              <SelectItem value="tiled">Tiled Text</SelectItem>
              <SelectItem value="both">Both</SelectItem>
            </SelectContent>
          </Select>
        </Row>

        <Row label="Logo Position">
          <Select value={cfg.position} onValueChange={(v) => save({ position: v })}>
            <SelectTrigger data-testid="select-wm-position" className="bg-[var(--tg-bg)] border-[var(--tg-border)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--tg-elevated)] border-[var(--tg-border)] text-[var(--tg-strong)]">
              <SelectItem value="top-left">Top Left</SelectItem>
              <SelectItem value="top-right">Top Right</SelectItem>
              <SelectItem value="bottom-left">Bottom Left</SelectItem>
              <SelectItem value="bottom-right">Bottom Right</SelectItem>
              <SelectItem value="center">Center</SelectItem>
            </SelectContent>
          </Select>
        </Row>

        <Row label={`Logo Size ${cfg.logo_scale}%`}>
          <Slider
            value={[cfg.logo_scale]}
            min={3}
            max={70}
            step={1}
            onValueChange={([v]) => setCfg({ ...cfg, logo_scale: v })}
            onValueCommit={([v]) => save({ logo_scale: v })}
            data-testid="slider-logo-scale"
          />
        </Row>

        <Row label={`Logo Opacity ${cfg.logo_opacity}%`}>
          <Slider
            value={[cfg.logo_opacity]}
            min={0}
            max={100}
            step={1}
            onValueChange={([v]) => setCfg({ ...cfg, logo_opacity: v })}
            onValueCommit={([v]) => save({ logo_opacity: v })}
            data-testid="slider-logo-opacity"
          />
        </Row>

        <Row label="Tiled Text">
          <Input
            data-testid="input-wm-text"
            value={cfg.text}
            onChange={(e) => setCfg({ ...cfg, text: e.target.value })}
            onBlur={() => save({ text: cfg.text })}
            placeholder="@mychannel"
            className="bg-[var(--tg-bg)] border-[var(--tg-border)] focus:border-[var(--c-sky)] font-mono"
          />
        </Row>

        <Row label={`Text Opacity ${cfg.text_opacity}%`}>
          <Slider
            value={[cfg.text_opacity]}
            min={0}
            max={100}
            step={1}
            onValueChange={([v]) => setCfg({ ...cfg, text_opacity: v })}
            onValueCommit={([v]) => save({ text_opacity: v })}
            data-testid="slider-text-opacity"
          />
        </Row>
      </Card>

      <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-5">
        <div className="font-head font-bold text-lg mb-4">Logo Asset</div>
        <div
          className="border-2 border-dashed border-[var(--tg-border)] rounded-lg p-8 grid place-items-center bg-[var(--tg-bg)] min-h-[220px]"
          data-testid="logo-preview"
        >
          {cfg.has_logo ? (
            <img
              src={`${API}/branding/logo?v=${logoVer}`}
              alt="logo"
              className="max-h-40 object-contain"
            />
          ) : (
            <div className="text-center">
              <ImagePlus className="h-8 w-8 mx-auto text-[var(--tg-faint)]" />
              <p className="text-sm text-[var(--tg-faint)] mt-2">No logo uploaded</p>
            </div>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={uploadLogo}
          data-testid="logo-file-input"
        />
        <Button
          onClick={() => fileRef.current?.click()}
          className="w-full mt-4 bg-[var(--c-sky)] hover:bg-[var(--c-sky-hover)] text-white font-semibold"
          data-testid="upload-logo-btn"
        >
          <Upload className="h-4 w-4 mr-2" /> Upload Logo (PNG recommended)
        </Button>
        <p className="text-xs text-[var(--tg-faint)] mt-3">
          Transparent PNGs give the cleanest overlay. Use the sliders to fine-tune size & opacity.
        </p>
      </Card>

      <Card className="bg-[var(--tg-surface)] border-[var(--tg-border)] p-5 lg:col-span-2 xl:col-span-3" data-testid="ocr-card">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[rgb(var(--c-teal-rgb)/0.10)] border border-[rgb(var(--c-teal-rgb)/0.30)] grid place-items-center">
              <ScanText className="h-5 w-5 text-[var(--c-teal)]" />
            </div>
            <div>
              <div className="font-head font-bold text-lg">In-Image Text Replacement</div>
              <p className="text-sm text-[var(--tg-faint)]">
                OCR detects text in copied images and rewrites it using your Replacement Rules.
              </p>
            </div>
          </div>
          <Switch
            checked={ocr?.enabled}
            onCheckedChange={(v) => saveOcr({ enabled: v })}
            data-testid="toggle-ocr"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div
            className={`rounded-lg border p-4 transition-colors ${ocr?.use_ai ? "border-[rgb(var(--c-sky-rgb)/0.40)] bg-[rgb(var(--c-sky-rgb)/0.05)]" : "border-[var(--tg-border)]"}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[var(--c-sky)]" />
                <span className="font-semibold text-sm">AI Recreation</span>
              </div>
              <Switch
                checked={ocr?.use_ai}
                onCheckedChange={(v) => saveOcr({ use_ai: v })}
                data-testid="toggle-ocr-ai"
              />
            </div>
            <p className="text-xs text-[var(--tg-faint)] mt-2">
              Gemini "Nano Banana" recreates the image with swapped text — best quality. Uses LLM credits.
            </p>
          </div>

          <div
            className={`rounded-lg border p-4 transition-colors ${ocr?.use_local ? "border-[rgb(var(--c-teal-rgb)/0.40)] bg-[rgb(var(--c-teal-rgb)/0.05)]" : "border-[var(--tg-border)]"}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PenTool className="h-4 w-4 text-[var(--c-teal)]" />
                <span className="font-semibold text-sm">Local Overlay Fallback</span>
              </div>
              <Switch
                checked={ocr?.use_local}
                onCheckedChange={(v) => saveOcr({ use_local: v })}
                data-testid="toggle-ocr-local"
              />
            </div>
            <p className="text-xs text-[var(--tg-faint)] mt-2">
              Covers old text and redraws the new text locally. Free & instant, best on flat backgrounds.
            </p>
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-[var(--tg-border)] p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ScanText className="h-4 w-4 text-[var(--c-sky)]" />
              <span className="font-semibold text-sm">AI Text Detection (stylized logos)</span>
            </div>
            <Switch
              checked={ocr?.ai_detect}
              onCheckedChange={(v) => saveOcr({ ai_detect: v })}
              data-testid="toggle-ai-detect"
            />
          </div>
          <p className="text-xs text-[var(--tg-faint)] mt-2">
            When Tesseract can't read decorative/logo text, a vision model detects your rule text (e.g. a "VAJRA TRADER" logo) so it still gets recreated. Uses LLM credits per image without a local match.
          </p>
        </div>

        <p className="text-xs text-[var(--tg-faint)] mt-4">
          Images are recreated when your replacement rules match the detected text (via OCR, or AI detection for stylized logos). Otherwise the original is copied untouched.
        </p>

        <div className="mt-5 pt-5 border-t border-[var(--tg-border)]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-[rgb(var(--c-amber-rgb)/0.10)] border border-[rgb(var(--c-amber-rgb)/0.30)] grid place-items-center">
                <UserRoundX className="h-5 w-5 text-[var(--c-amber)]" />
              </div>
              <div>
                <div className="font-semibold text-sm">Remove People from Images</div>
                <p className="text-xs text-[var(--tg-faint)]">
                  When a real person is detected, AI erases them and rebuilds the background — text swaps and logo still apply.
                </p>
              </div>
            </div>
            <Switch
              checked={ocr?.remove_humans}
              onCheckedChange={(v) => saveOcr({ remove_humans: v })}
              data-testid="toggle-remove-humans"
            />
          </div>

          <div
            className={`mt-4 rounded-lg border p-4 transition-colors ${ocr?.human_confirm_ai ? "border-[rgb(var(--c-sky-rgb)/0.40)] bg-[rgb(var(--c-sky-rgb)/0.05)]" : "border-[var(--tg-border)]"} ${ocr?.remove_humans ? "" : "opacity-40 pointer-events-none"}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[var(--c-sky)]" />
                <span className="font-semibold text-sm">Confirm with AI</span>
              </div>
              <Switch
                checked={ocr?.human_confirm_ai}
                onCheckedChange={(v) => saveOcr({ human_confirm_ai: v })}
                data-testid="toggle-human-ai"
              />
            </div>
            <p className="text-xs text-[var(--tg-faint)] mt-2">
              A vision model reads the image and reliably detects people — even small, partial, or wearing sunglasses — that basic local detection misses. Turn off to use fast local-only detection (less accurate).
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
